/**
 * GenerateUserContentShareDataUseCase
 *
 * Computes share title, text, URL and downloads the thumbnail
 * for use with both the native Web Share API and the social share sheet.
 */

import { env } from '@/config/env';
import { remoteConfigService } from '@/domain/services/remoteConfig/remoteConfigService';
import { MediaUtils } from '@/domain/utils/mediaUtils';
import type { UserMediaEntity } from '@/domain/entities/userMedia/userMediaEntity';
import { downloadImageUseCase } from '../storage/downloadImageUseCase';
import { canShareFilesUseCase } from './canShareFilesUseCase';
import type { ShareContent } from './shareContent';

class GenerateUserContentShareDataUseCase {
  async execute(media: UserMediaEntity): Promise<ShareContent> {
    const shareUrl = `${env.DOMAIN_NAME}/${media.shareLink}`;
    const userName = media.userName ?? '';
    const title = `Expause - ${userName}`;
    const maxChars = remoteConfigService.shareContentMaxCharacters;
    const description = media.description?.trim() ?? '';
    const text = description ? description.substring(0, Math.min(description.length, maxChars)) : '';

    let file: File | null = null;
    if (media.relativeThumbnailPath && !media.isLocal) {
      // Content thumbnails are CDN-served via real path separators
      // (Flutter `Uri.decodeFull`), so they are decoded. Default-bucket
      // thumbnails are Firebase download URLs whose object path must stay
      // `%2F`-encoded, and whose stored value carries the duplicated `o/`
      // download segment — both handled by `joinDefaultBucketUrl`.
      const thumbnailUrl = media.isContent
        ? `${env.CDN_NAME}${decodeURIComponent(media.relativeThumbnailPath)}`
        : MediaUtils.joinDefaultBucketUrl(media.relativeThumbnailPath);
      const downloadedFile = await downloadImageUseCase.execute(thumbnailUrl, 'thumbnail.jpg');
      file = downloadedFile && canShareFilesUseCase.execute([downloadedFile]) ? downloadedFile : null;
    }

    return { title, text, shareUrl, file };
  }
}

export const generateUserContentShareDataUseCase = new GenerateUserContentShareDataUseCase();
